/**
 * CoffeeTooltip Component
 * Playful animated tooltip that slides in from the right, beside the coffee icon
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface CoffeeTooltipProps {
  /** Delay before showing the tooltip (ms) */
  delay?: number;
  /** How long to display before hiding (ms) */
  duration?: number;
  /** Ko-fi/BuyMeACoffee URL */
  href?: string;
}

export function CoffeeTooltip({
  delay = 800,
  duration = 5000,
  href = 'https://ko-fi.com/nilocoffeetime'
}: CoffeeTooltipProps) {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Show tooltip after initial delay
    const showTimer = setTimeout(() => {
      setIsVisible(true);
    }, delay);

    // Start exit animation after duration
    const exitTimer = setTimeout(() => {
      setIsExiting(true);
    }, delay + duration);

    // Fully hide after exit animation completes
    const hideTimer = setTimeout(() => {
      setIsVisible(false);
    }, delay + duration + 500);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(exitTimer);
      clearTimeout(hideTimer);
    };
  }, [delay, duration]);

  if (!isVisible) return null;

  return (
    <>
      {/* CSS Keyframes - slide in from right */}
      <style>{`
        @keyframes slideInFromRight {
          0% {
            transform: translateX(100%);
            opacity: 0;
          }
          60% {
            transform: translateX(-8px);
            opacity: 1;
          }
          80% {
            transform: translateX(4px);
          }
          100% {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slideOutToRight {
          0% {
            transform: translateX(0);
            opacity: 1;
          }
          100% {
            transform: translateX(100%);
            opacity: 0;
          }
        }

        .tooltip-enter {
          animation: slideInFromRight 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }

        .tooltip-exit {
          animation: slideOutToRight 0.4s ease-in forwards;
        }
      `}</style>

      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="coffee-tooltip"
        className={`
          absolute top-14 right-4 sm:top-16 sm:right-6 z-40
          flex items-center gap-2
          px-4 py-2
          bg-slate-800
          border border-orange-500/50
          rounded-full
          shadow-lg shadow-black/30
          cursor-pointer
          hover:bg-slate-700 hover:border-orange-500
          transition-all duration-200
          ${isExiting ? 'tooltip-exit' : 'tooltip-enter'}
        `}
      >
        {/* Text */}
        <span className="text-slate-100 font-medium text-sm whitespace-nowrap">
          {t('pages.login.coffeeTooltip')}
        </span>

        {/* Arrow pointing up to coffee icon */}
        <span className="text-orange-400">↑</span>
      </a>
    </>
  );
}
